import React, { useRef, useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "../hooks/useTranslation";
import {
  Play,
  Pause,
  Square,
  VolumeX,
  Volume2,
  Heart,
  Loader2,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
} from "lucide-react";
import { getShots, likeShot, Shot } from "../services/shotsApi";

// ---------------------------------------------------------------------------
// DEV: replace these URLs with your actual S3 links to test locally.
// Set USE_STATIC_DATA = false to fetch from the API instead.
// ---------------------------------------------------------------------------
export const USE_STATIC_DATA = false;

export const STATIC_SHOTS: Shot[] = [
  {
    id: "dev-1",
    title: "Nike Air Force 1",
    videoKey: "",
    posterKey: null,
    videoUrl: "https://assets.lavetesbaskets.com/shorts/LTB10.mov",
    posterUrl: "https://your-bucket.s3.amazonaws.com/shots/posters/shot1.jpg",
    likes: 24,
    displayOrder: 0,
    isActive: true,
    createdAt: "",
    updatedAt: "",
  },
  {
    id: "dev-2",
    title: "Adidas Yeezy",
    videoKey: "",
    posterKey: null,
    videoUrl: "https://assets.lavetesbaskets.com/shorts/LTB10.mov",
    posterUrl: "https://your-bucket.s3.amazonaws.com/shots/posters/shot2.jpg",
    likes: 18,
    displayOrder: 1,
    isActive: true,
    createdAt: "",
    updatedAt: "",
  },
  {
    id: "dev-3",
    title: "Jordan 1 Retro",
    videoKey: "",
    posterKey: null,
    videoUrl: "https://assets.lavetesbaskets.com/shorts/LTB10.mov",
    posterUrl: "https://your-bucket.s3.amazonaws.com/shots/posters/shot3.jpg",
    likes: 31,
    displayOrder: 2,
    isActive: true,
    createdAt: "",
    updatedAt: "",
  },
  {
    id: "dev-4",
    title: "New Balance 550",
    videoKey: "",
    posterKey: null,
    videoUrl: "https://assets.lavetesbaskets.com/shorts/LTB10.mov",
    posterUrl: "https://your-bucket.s3.amazonaws.com/shots/posters/shot4.jpg",
    likes: 12,
    displayOrder: 3,
    isActive: true,
    createdAt: "",
    updatedAt: "",
  },
  {
    id: "dev-5",
    title: "Converse Chuck 70",
    videoKey: "",
    posterKey: null,
    videoUrl: "https://assets.lavetesbaskets.com/shorts/LTB10.mov",
    posterUrl: "https://your-bucket.s3.amazonaws.com/shots/posters/shot5.jpg",
    likes: 9,
    displayOrder: 4,
    isActive: true,
    createdAt: "",
    updatedAt: "",
  },
];
// ---------------------------------------------------------------------------

const LIKED_KEY = "ltb_liked_shots";

export function getLikedSet(): Set<string> {
  try {
    const raw = localStorage.getItem(LIKED_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

export function saveLikedSet(set: Set<string>) {
  localStorage.setItem(LIKED_KEY, JSON.stringify(Array.from(set)));
}

interface VideoCardProps {
  shot: Shot;
  globalMuted: boolean;
  liked: boolean;
  onLike: (id: string) => void;
  activeId: string | null;
  onActivate: (id: string) => void;
  onDeactivate: (id: string) => void;
}

export const VideoCard: React.FC<VideoCardProps> = ({
  shot,
  globalMuted,
  liked,
  onLike,
  activeId,
  onActivate,
  onDeactivate,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [localLikes, setLocalLikes] = useState(shot.likes);
  const [liking, setLiking] = useState(false);

  const isActive = activeId === shot.id;

  // Sync mute state with the global toggle
  useEffect(() => {
    setMuted(globalMuted);
    if (videoRef.current) videoRef.current.muted = globalMuted;
  }, [globalMuted]);

  // Sync play/pause to isActive: play when becomes active, pause when not
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (isActive) {
      if (v.paused) {
        v.play().catch(() => {});
      }
    } else {
      if (!v.paused) v.pause();
    }
  }, [isActive]);

  // Auto-pause when card scrolls out of view (and stop being active)
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!videoRef.current) return;
        if (!entry.isIntersecting && !videoRef.current.paused) {
          videoRef.current.pause();
          onDeactivate(shot.id);
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [shot.id, onDeactivate]);

  const handlePlay = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!videoRef.current) return;
    onActivate(shot.id);
    videoRef.current.play().catch(() => {});
  };

  const handlePause = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.pause();
  };

  const handleStop = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.pause();
    videoRef.current.currentTime = 0;
    onDeactivate(shot.id);
  };

  const togglePlayPause = () => {
    if (isPlaying) handlePause();
    else handlePlay();
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    const next = !videoRef.current.muted;
    videoRef.current.muted = next;
    setMuted(next);
  };

  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (liked || liking) return;
    if (USE_STATIC_DATA) {
      setLocalLikes((n) => n + 1);
      onLike(shot.id);
      return;
    }
    setLiking(true);
    try {
      const newCount = await likeShot(shot.id);
      setLocalLikes(newCount);
      onLike(shot.id);
    } catch {
      // silent fail
    } finally {
      setLiking(false);
    }
  };

  return (
    <div
      ref={cardRef}
      className={`relative flex-shrink-0 w-[260px] sm:w-[300px] rounded-3xl overflow-hidden shadow-2xl transition-all duration-500 ${
        isActive
          ? "ring-2 ring-accent-teal/60 shadow-accent-teal/20 scale-[1.02]"
          : "ring-1 ring-gray-200"
      }`}
      style={{ aspectRatio: "9/16" }}
    >
      {/* Subtle accent glow when active */}
      {isActive && (
        <div className="pointer-events-none absolute -inset-1 rounded-3xl bg-gradient-to-br from-accent-teal/30 to-accent-coral/30 blur-xl -z-10" />
      )}

      <div
        className="absolute inset-0 bg-gray-900 cursor-pointer"
        onClick={togglePlayPause}
      >
        <video
          ref={videoRef}
          src={shot.videoUrl}
          poster={shot.posterUrl ?? undefined}
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover"
          onPlay={() => {
            setIsPlaying(true);
            onActivate(shot.id);
          }}
          onPause={() => setIsPlaying(false)}
        />

        {/* Top gradient for controls */}
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/60 to-transparent pointer-events-none" />
        {/* Bottom gradient for title/likes */}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none" />

        {/* Big centered play overlay (only when paused) */}
        {!isPlaying && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center shadow-2xl">
              <Play className="w-8 h-8 sm:w-10 sm:h-10 text-white fill-white ml-1" />
            </div>
          </div>
        )}
      </div>

      {/* Top-left: Stop button (only when playing or paused-with-progress) */}
      {/* <button
        onClick={handleStop}
        className="absolute top-3 left-3 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-md border border-white/15 flex items-center justify-center transition-all hover:scale-110"
        aria-label="Stop"
        title="Stop"
      >
        <Square className="w-3.5 h-3.5 text-white fill-white" />
      </button> */}

      {/* Top-right: Mute / Unmute (always visible) */}
      <button
        onClick={toggleMute}
        className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-md border border-white/15 flex items-center justify-center transition-all hover:scale-110"
        aria-label={muted ? "Unmute" : "Mute"}
        title={muted ? "Unmute" : "Mute"}
      >
        {muted ? (
          <VolumeX className="w-4 h-4 text-white" />
        ) : (
          <Volume2 className="w-4 h-4 text-white" />
        )}
      </button>

      {/* Bottom: title + like */}
      <div className="absolute bottom-0 left-0 right-0 px-4 pb-4 pt-3 flex items-end justify-between gap-3 pointer-events-none">
        <p className="text-white text-sm font-bold leading-tight flex-1 min-w-0 truncate drop-shadow-lg">
          {shot.title}
        </p>
        {/* <button
          onClick={handleLike}
          disabled={liked || liking}
          className="pointer-events-auto flex flex-col items-center gap-0.5 transition-transform active:scale-90 disabled:cursor-default"
          aria-label="Like"
        >
          <div
            className={`w-10 h-10 rounded-full backdrop-blur-md border flex items-center justify-center transition-all ${
              liked
                ? "bg-accent-coral/20 border-accent-coral/60"
                : "bg-black/40 border-white/15 hover:bg-black/60 hover:scale-110"
            }`}
          >
            {liking ? (
              <Loader2 className="w-4 h-4 text-white animate-spin" />
            ) : (
              <Heart
                className={`w-5 h-5 transition-colors ${
                  liked ? "fill-accent-coral text-accent-coral" : "text-white"
                }`}
              />
            )}
          </div>
          <span className="text-white text-[11px] font-bold drop-shadow">
            {localLikes}
          </span>
        </button> */}
      </div>
    </div>
  );
};

const ShotsSection: React.FC = () => {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const [allShots, setAllShots] = useState<Shot[]>([]);
  const [loading, setLoading] = useState(true);
  const [globalMuted, setGlobalMuted] = useState(true);
  const [likedIds, setLikedIds] = useState<Set<string>>(getLikedSet);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [userScrolled, setUserScrolled] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Display only the latest 5 on landing
  const shots = allShots.slice(0, 5);

  useEffect(() => {
    if (USE_STATIC_DATA) {
      setAllShots(STATIC_SHOTS);
      setLoading(false);
      return;
    }
    getShots().then((data) => {
      setAllShots(data.shots);
      setLoading(false);
    });
  }, []);

  // Initial autoplay: first card plays on mount
  useEffect(() => {
    if (!loading && shots.length > 0 && activeId === null && !userScrolled) {
      setActiveId(shots[0]!.id);
    }
  }, [loading, shots, activeId, userScrolled]);

  // Detect first user scroll on the carousel
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => setUserScrolled(true);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading]);

  // Mobile: as user scrolls, the most-centered card becomes active
  useEffect(() => {
    if (!userScrolled) return;
    const isMobile = window.matchMedia("(max-width: 639px)").matches;
    if (!isMobile) return;
    const el = scrollRef.current;
    if (!el) return;
    const updateActive = () => {
      const rect = el.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      const cards = el.querySelectorAll<HTMLElement>("[data-shot-id]");
      let bestId: string | null = null;
      let bestDist = Infinity;
      cards.forEach((c) => {
        const r = c.getBoundingClientRect();
        const cCenter = r.left + r.width / 2;
        const d = Math.abs(cCenter - center);
        if (d < bestDist) {
          bestDist = d;
          bestId = c.dataset["shotId"] ?? null;
        }
      });
      if (bestId) setActiveId(bestId);
    };
    updateActive();
    el.addEventListener("scroll", updateActive, { passive: true });
    return () => el.removeEventListener("scroll", updateActive);
  }, [userScrolled, shots]);

  const handleLike = useCallback((id: string) => {
    setLikedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      saveLikedSet(next);
      return next;
    });
  }, []);

  const handleActivate = useCallback((id: string) => {
    setActiveId(id);
  }, []);

  const handleDeactivate = useCallback((id: string) => {
    setActiveId((current) => (current === id ? null : current));
  }, []);

  const scrollLeft = () =>
    scrollRef.current?.scrollBy({ left: -320, behavior: "smooth" });
  const scrollRight = () =>
    scrollRef.current?.scrollBy({ left: 320, behavior: "smooth" });

  if (!loading && shots.length === 0) return null;

  return (
    <section className="relative isolate bg-white overflow-hidden py-16 sm:py-24">
      {/* Subtle decorative blurred circles */}
      <div className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-accent-teal/10 blur-3xl" />
      <div className="pointer-events-none absolute top-1/3 -right-32 h-96 w-96 rounded-full bg-accent-coral/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 left-1/3 h-80 w-80 rounded-full bg-cyan-400/5 blur-3xl" />

      <div className="container mx-auto px-5 max-w-7xl relative">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-14">
          {/* <div className="inline-flex items-center gap-2 bg-accent-teal/10 px-4 py-1.5 rounded-full text-xs sm:text-sm mb-5 border border-accent-teal/20">
            <Sparkles className="w-3.5 h-3.5 text-accent-teal" />
            <span className="text-accent-teal font-semibold">
              {t("shots.badge")}
            </span>
          </div> */}
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight text-gray-900">
            {t("shots.title")}
          </h2>
          <span className="block mx-auto mt-4 h-1 w-32 rounded-full bg-gradient-to-r from-accent-teal to-accent-coral" />
          <p className="mt-5 text-base sm:text-lg text-gray-600 max-w-xl mx-auto">
            {t("shots.subtitle")}
          </p>
        </div>

        {/* Loading skeleton */}
        {loading && (
          <div className="flex gap-5 overflow-hidden justify-center">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="flex-shrink-0 w-[260px] sm:w-[300px] rounded-3xl bg-gray-100 border border-gray-200 animate-pulse"
                style={{ aspectRatio: "9/16" }}
              />
            ))}
          </div>
        )}

        {/* Video carousel */}
        {!loading && (
          <div className="relative">
            {/* Side fades */}
            <div className="hidden sm:block pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-white to-transparent z-10" />
            <div className="hidden sm:block pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-white to-transparent z-10" />

            <button
              onClick={scrollLeft}
              className="hidden sm:flex absolute -left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white border border-gray-200 items-center justify-center text-gray-700 hover:text-gray-900 hover:border-gray-300 hover:scale-110 transition-all shadow-lg"
              aria-label={t("common.prev") || "Previous"}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={scrollRight}
              className="hidden sm:flex absolute -right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white border border-gray-200 items-center justify-center text-gray-700 hover:text-gray-900 hover:border-gray-300 hover:scale-110 transition-all shadow-lg"
              aria-label={t("common.next") || "Next"}
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            <div
              ref={scrollRef}
              className="flex gap-5 overflow-x-auto pb-4 pt-2 px-1 snap-x snap-mandatory"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {shots.map((shot) => (
                <div
                  key={shot.id}
                  data-shot-id={shot.id}
                  className="snap-center first:pl-4 last:pr-4"
                >
                  <VideoCard
                    shot={shot}
                    globalMuted={globalMuted}
                    liked={likedIds.has(shot.id)}
                    onLike={handleLike}
                    activeId={activeId}
                    onActivate={handleActivate}
                    onDeactivate={handleDeactivate}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* View more button */}
        {!loading && shots.length > 0 && (
          <div className="mt-10 sm:mt-12 flex justify-center">
            <Link
              to={`/${lang || "fr"}/shots`}
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-header-gradient text-white font-semibold text-sm sm:text-base shadow-lg hover:shadow-xl hover:scale-105 transition-all"
            >
              <span>{t("shots.viewMore")}</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default ShotsSection;
